import { FC, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { AlertTriangle } from 'lucide-react';
import { IndiceDto } from 'api/indice';
import { indicesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { toDayInput, toDayPayload } from 'core/utils/dates';
import { formatIndiceValue } from '../indiceValues';

interface ValueFormValues {
  startDate: string;
  value: number | '';
}

const validationSchema = Yup.object().shape({
  startDate: Yup.string().required('La date de début est requise'),
  value: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .required('La valeur est requise'),
});

interface IndiceValueModalProps {
  indice: IndiceDto;
  onSuccess: () => void;
}

/** Ajout d'une valeur datée à un indice. */
export const IndiceValueModal: FC<IndiceValueModalProps> = ({ indice, onSuccess }) => {
  const formikRef = useRef<FormikProps<ValueFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: ValueFormValues = { startDate: '', value: '' };

  const handleSubmit = async (values: ValueFormValues) => {
    if (isSubmitting) return;

    const startDate = toDayPayload(values.startDate);
    if (!startDate) {
      toast.error('La date de début est invalide');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await indicesAPI.addIndiceValue({
        indiceId: indice.id,
        // Date calendaire en chaîne de bout en bout : le back ignore l'heure et ne
        // convertit aucun fuseau, sérialiser un Date ferait glisser le jour.
        ajouterValeurIndiceCommand: { startDate, value: Number(values.value) },
      });

      if (response.isSuccess) {
        toast.success('Valeur enregistrée');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (error) {
      console.error("Ajout de la valeur impossible", error);
      toast.error("L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        // Le back remplace la valeur existante à cette date plutôt que d'en créer une
        // seconde : autant l'annoncer avant l'enregistrement.
        const day = toDayPayload(values.startDate);
        const replaced = day
          ? (indice.values ?? []).find((item) => toDayInput(item.startDate) === day)
          : undefined;

        return (
          <ModalShell
            title="Ajouter une valeur"
            subtitle={indice.label || indice.code || undefined}
            submit={{
              label: 'Enregistrer',
              onClick: () => formikRef.current?.submitForm(),
              disabled: isSubmitting,
            }}
          >
            <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
              <FormikInputText
                name="startDate"
                label="Date de début de validité"
                type="date"
                required
              />

              <FormikInputText
                name="value"
                label="Valeur"
                type="number"
                step="0.001"
                required
                placeholder="0,000"
              />
              <p className="-mt-3 text-xs text-gray-400">
                Jusqu'à trois décimales. Cette valeur s'appliquera à partir de la date
                indiquée, jusqu'à la valeur suivante.
              </p>

              {replaced && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  Une valeur de {formatIndiceValue(replaced.value)} existe déjà à cette
                  date : elle sera remplacée, et non ajoutée en double.
                </p>
              )}
            </Form>
          </ModalShell>
        );
      }}
    </Formik>
  );
};
